import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Avatar,
  IconButton,
  Drawer,
  Popover,
  Divider,
  Select,
  MenuItem,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { useAuth } from "../context/AuthContext";
import { useZone } from "../context/ZoneContext";
import { Factory, CloudSync, LogOut, Mail } from "lucide-react";
import { Sparkles } from "@mynaui/icons-react";
import { getNavigationItems } from "../config/roleConfig";
import { ROLES } from "../config/roleConfig";
import ChatBotModal from "../../features/chat/ChatBotModal";

const zoneSelectSx = {
  flexShrink: 0,
  minWidth: 200,
  height: 36,
  bgcolor: "#FFFFFF",
  border: "1px solid #E2E8F0",
  borderRadius: "6px",
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& .MuiSelect-select": {
    py: "6px",
    px: "12px",
    fontSize: "13px",
    fontWeight: 500,
    lineHeight: "20px",
    color: "#0F172B",
  },
  "& .MuiSelect-icon": { color: "#6B7280", right: 8, fontSize: "15px" },
};

const zoneMenuProps = {
  PaperProps: {
    sx: { "& .MuiMenuItem-root": { fontSize: "14px" } },
  },
};

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { selectedZone, setSelectedZone, locations, lastSync } = useZone();
  const [chatOpen, setChatOpen] = useState(false);

  const isAssetsDetail = /^\/assets\/[^/]+$/.test(location.pathname);
  const isAssetDetail = /^\/asset\/[^/]+$/.test(location.pathname);
  const isRequestDetail = /^\/requests\/[^/]+$/.test(location.pathname);
  const isLgUp = useMediaQuery("(min-width:1100px)");
  const isSmUp = useMediaQuery("(min-width:520px)");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [avatarAnchor, setAvatarAnchor] = useState(null);

  const handleLogout = async () => {
    setAvatarAnchor(null);
    await logout();
    navigate("/login");
  };

  const navigationItems = getNavigationItems(user?.role);

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const formattedLastSync = lastSync
    ? new Date(lastSync).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
    : "—";

  const showZoneDropdown =
    !isAssetsDetail && !isRequestDetail && !isAssetDetail;

  const renderZoneDropdown = (onChange) => (
    <Select
      value={selectedZone}
      onChange={onChange}
      size="small"
      IconComponent={UnfoldMoreIcon}
      sx={zoneSelectSx}
      MenuProps={zoneMenuProps}
    >
      {locations.flatMap((plant) => [
        <MenuItem
          key={`plant-${plant.id}`}
          disabled
          sx={{ fontWeight: 600, fontSize: "12px", color: "#6B7280", opacity: "1 !important", py: 0.5 }}
        >
          {plant.name}
        </MenuItem>,
        ...plant.areas.map((area) => (
          <MenuItem key={area.code} value={area.code} sx={{ pl: 3 }}>
            {area.name}
          </MenuItem>
        )),
      ])}
    </Select>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      {/* ================= APP BAR ================= */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: "#FFFFFF",
          borderTop: "1px solid #E2E8F0",
          borderBottom: "1px solid #E2E8F0",
          boxShadow: "0px 4px 4px rgba(168,168,168,0.04)",
        }}
      >
        <Toolbar disableGutters sx={{ height: 57, px: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              position: "relative",
              flexWrap: "nowrap",
            }}
          >
            {/* ================= LEFT SIDE ================= */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1, lg: 2 }, // reduced from lg: 8
                flexGrow: isLgUp ? 1 : 0,
                flexShrink: isLgUp ? 1 : 0,
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              {/* Hamburger */}
              {!isLgUp && (
                <IconButton onClick={() => setDrawerOpen(true)} sx={{ flexShrink: 0, p: 0.5 }}>
                  <MenuIcon />
                </IconButton>
              )}

              {/* Logo */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: "#3A9432",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    borderRadius: 0.5,
                  }}
                >
                  <Factory size={26} color="#fff" />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "18px",
                    whiteSpace: "nowrap",
                    color: "black",
                    "@media (max-width: 700px)": { display: "none" },
                  }}
                >
                  Maintenance AI
                </Typography>
              </Box>

              {/* LG: Zone dropdown + Nav items */}
              {isLgUp && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>

                  {/* Zone dropdown — shown first for ALL roles */}
                  {showZoneDropdown &&
                    (user?.role === ROLES.PLANT_DIRECTOR ||
                      user?.role === ROLES.SUPERVISOR ||
                      user?.role === ROLES.TECHNICIAN) &&
                    renderZoneDropdown((e) => {
                      const areaCode = e.target.value;
                      if (user?.role === ROLES.PLANT_DIRECTOR) {
                        const plant = locations.find((p) =>
                          p.areas.some((a) => a.code === areaCode)
                        );
                        setSelectedZone(areaCode, plant?.id);
                      } else {
                        setSelectedZone(areaCode);
                      }
                    })}

                  {/* Nav items after dropdown */}
                  {navigationItems.map((item) => {
                    const isActive =
                      location.pathname === item.path ||
                      location.pathname.startsWith(item.path + "/");
                    return (
                      <Button
                        key={item.path}
                        onClick={() => navigate(item.path)}
                        sx={{
                          flexShrink: 0,
                          px: 1.5,
                          py: 0.75,
                          borderRadius: 0.5,
                          fontSize: "0.875rem",
                          fontWeight: isActive ? 600 : 400,
                          color: isActive ? "text.primary" : "text.secondary",
                          bgcolor: isActive ? "#F3F4F6" : "transparent",
                          textTransform: "none",
                          "&:hover": { bgcolor: "#F3F4F6" },
                        }}
                      >
                        {item.label}
                      </Button>
                    );
                  })}
                </Box>
              )}
            </Box>

            {/* Spacer */}
            <Box sx={{ flexGrow: 1 }} />

            {/* Zone Dropdown - centered on md/sm screens */}
            {!isLgUp &&
              isSmUp &&
              showZoneDropdown &&
              (user?.role === ROLES.SUPERVISOR ||
                user?.role === ROLES.TECHNICIAN ||
                user?.role === ROLES.PLANT_DIRECTOR) && (
                <>
                  {renderZoneDropdown((e) => setSelectedZone(e.target.value))}
                  <Box sx={{ flexGrow: 1 }} />
                </>
              )}

            {/* Fixed Gap */}
            <Box sx={{ width: { xs: 8, sm: 16, md: 24 } }} />

            {/* ================= RIGHT SIDE ================= */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1, sm: 1.5, md: 2 },
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  pr: { md: 2 },
                  borderRight: { md: "1px solid #E2E8F0" },
                }}
              >
                <CloudSync size={20} color="black" />
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: "11px", sm: "12px", md: "12px", lg: "13px" },
                    whiteSpace: "nowrap",
                  }}
                >
                  Last Sync - {formattedLastSync}
                </Typography>
              </Box>

              <Box
                onClick={() => setChatOpen(true)}
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: "#0F172B",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "50%",
                  cursor: "pointer",
                  "&:hover": { bgcolor: "#1E293B" },
                }}
              >
                <Sparkles size={18} color="#fff" />
              </Box>

              <Avatar
                onClick={(e) => setAvatarAnchor(e.currentTarget)}
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: "#3A9432",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {getInitials(user?.name)}
              </Avatar>

              <Popover
                open={Boolean(avatarAnchor)}
                anchorEl={avatarAnchor}
                onClose={() => setAvatarAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{
                  paper: {
                    sx: {
                      mt: 1.5,
                      borderRadius: "12px",
                      border: "1px solid #E2E8F0",
                      boxShadow: "0px 4px 16px rgba(0,0,0,0.08)",
                      minWidth: 240,
                      overflow: "hidden",
                    },
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: "16px 16px 14px" }}>
                  <Avatar
                    sx={{ width: 36, height: 36, bgcolor: "#3A9432", fontSize: "13px", fontWeight: 600 }}
                  >
                    {getInitials(user?.name)}
                  </Avatar>
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#020618", lineHeight: 1.3 }}>
                      {user?.name || "User"}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
                      <Mail size={12} color="#6B7280" />
                      <Typography sx={{ fontSize: "12px", color: "#6B7280", lineHeight: 1.3 }}>
                        {user?.email || "user@example.com"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Divider sx={{ borderColor: "#E2E8F0" }} />

                <Box sx={{ p: "6px 8px" }}>
                  <Button
                    fullWidth
                    onClick={() => { setAvatarAnchor(null); handleLogout(); }}
                    startIcon={<LogOut size={16} />}
                    sx={{
                      justifyContent: "flex-start",
                      textTransform: "none",
                      color: "#020618",
                      fontSize: "13px",
                      fontWeight: 500,
                      borderRadius: "8px",
                      px: 1.5,
                      py: 0.75,
                      "&:hover": { bgcolor: "#F3F4F6" },
                    }}
                  >
                    Logout
                  </Button>
                </Box>
              </Popover>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* ================= DRAWER ================= */}
      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 250, p: 2 }}>
          {!isSmUp && showZoneDropdown &&
            (user?.role === ROLES.SUPERVISOR ||
              user?.role === ROLES.TECHNICIAN ||
              user?.role === ROLES.PLANT_DIRECTOR) && (
              <Select
                value={selectedZone}
                onChange={(e) => setSelectedZone(e.target.value)}
                size="small"
                fullWidth
                IconComponent={UnfoldMoreIcon}
                sx={{ ...zoneSelectSx, mb: 2, minWidth: "unset" }}
                MenuProps={zoneMenuProps}
              >
                {locations.flatMap((plant) => [
                  <MenuItem key={`plant-${plant.id}`} disabled sx={{ fontWeight: 600, fontSize: "12px", color: "#6B7280", opacity: "1 !important", py: 0.5 }}>
                    {plant.name}
                  </MenuItem>,
                  ...plant.areas.map((area) => (
                    <MenuItem key={area.code} value={area.code} sx={{ pl: 3 }}>
                      {area.name}
                    </MenuItem>
                  )),
                ])}
              </Select>
            )}
          {navigationItems.map((item) => (
            <Button
              key={item.path}
              fullWidth
              onClick={() => { navigate(item.path); setDrawerOpen(false); }}
              sx={{ justifyContent: "flex-start", mb: 1, textTransform: "none" }}
            >
              {item.label}
            </Button>
          ))}
        </Box>
      </Drawer>

      {/* ================= CHAT BOT ================= */}
      <ChatBotModal open={chatOpen} onClose={() => setChatOpen(false)} />

      {/* ================= CONTENT ================= */}
      <Box component="main" sx={{ flexGrow: 1 }}>
        {children}
      </Box>
    </Box>
  );
};

export default Layout;